import type { Ban, Collapsed } from "@p0security/util/types/util";

import type {
  ComponentMaturity,
  IntegrationStatus,
} from "../integrations/shared.ts";
import type { DevEnv } from "../types/environment.ts";

export type InstallContext<D> = Collapsed<
  {
    config: D;
    tenantId: string;
  } & DevEnv
>;

// --- Schema definition ---

/** Construct an installation specification.
 *
 * An installation specification is a mapping of component keys to {@link ItemComponent}
 * specifications.
 *
 * Component keys may not include the reserved "integration" key.
 */
export const InstallSpec = <I extends InstallSpec>(
  spec: I
): I extends { integration: any } ? never : Readonly<I> => spec as any;
export type InstallSpec = Readonly<Record<string, ItemComponent<EmptySchema>>>;

/** Construct an item configuration component.
 *
 * An item configuration component bundles together multiple configuration elements,
 * determined by this component's `schema`. The resulting configuration is an object
 * whose property names are equal to the schema's keys, and property values determined
 * from the schema's corresponding element values.
 *
 * The item configuration is also an element itself; this element defines how to
 * determine the item's identifier.
 */
export const ItemComponent = <I extends ItemComponent<EmptySchema>>(
  component: I extends ItemComponent<infer S>
    ? // This wonkiness is necessary to get TS to prevent "id", "label", or "step" from appearing in schema
      S extends Ban<S, "id" | "label" | "state" | "step">
      ? I
      : // Indicate to user which key(s) are offending
        { schema: Omit<S, "id" | "label" | "state" | "step"> }
    : never
): Readonly<I> => component;

export const Component = <C extends Component<EmptySchema>>(
  component: C
): Readonly<C> => component;

/** Construct a configuration element.
 *
 * Configuration elements correspond to a property in the item's configuration.
 * Corresponding configuration values are determined via user input, depending on
 * the type of element.
 */
export const Element = <E extends Element>(element: E): Readonly<E> => element;

/** Construct an integration install option.
 *
 * An option is just a {@link Component}, but can not have a property named
 * "type" in its schema.
 *
 * The option's configuration value is an object with property `type` equal
 * to this key, and remaining properties generated by mapping the schema
 * values according to {@link Element}.
 */
export const Option = <O extends Option<EmptySchema>>(
  option: O extends Option<infer S>
    ? // Prevents user from creating option named "type"
      S extends Ban<S, "type">
      ? O
      : // Provide friendly feedback to user indicating that "type" key is the problem
        { schema: Option<Omit<S, "type">>["schema"] }
    : never
): Readonly<O> => option;

/** Represents an {@link OptionsElement}'s options
 *
 * An option with "dev" maturity is offered only in devLike environments, as
 * an {@link ItemComponent}'s is.
 */
export type Option<S extends EmptySchema> = Readonly<
  Component<S> & { maturity: ComponentMaturity }
>;

/** A configuration element whose value is determined by executing a
 * callback on a passed {@link InstallContext}.
 */
export type DynamicElement = {
  type: "dynamic";
};

/** An element whose value is determined via user text input. */
type StringElement = {
  type: "string";
  multiline?: boolean;
  placeholder?: string;
};

/** An element whose value is determined by the backend. */
type GeneratedElement = { type: "generated" };

/** An Element whose value is determined by user switch input. */
type SwitchElement = { type: "switch" };

/** An element that is hidden from the user.
 *
 * Use a HiddenElement to store data without a UI.
 */
type HiddenElement = { type: "hidden" };

/** Represents an item component with only one install.
 *
 * Use SingletonItem for organization-wide components.
 */
type SingletonItem = { type: "singleton" };

/** An Element that is obfuscated from the user
 *
 * Use an EncryptedElement to store sensitive data.
 * */
export type EncryptedElement = {
  type: "encrypted";
};

/** A configuration element whose value is determined by user selection from a list
 * of statically known choices.
 */
export type OptionsElement<
  O extends Readonly<Record<string, Option<EmptySchema>>>,
> = {
  type: "select";
  default: string;
  options: O;
};

type BaseElement = {
  /** Short display label for this element */
  label: string;
  /** A longer human-readable description to display alongside the label */
  description?: string;
  /** If true, this element is hidden */
  hidden?: true;
  /** If true, this element may be omitted from the configuration */
  optional?: true;
  /** Indicates at which install step this element's config value is fixed
   *
   * Default value is "configure"
   */
  step?: "configure" | "new";
};

type GenericTypedElement<T> = BaseElement & {
  /** Performs config item form-field validation
   */
  validator?: Validator<T>;
};

export type Element = Readonly<
  | (BaseElement & SwitchElement)
  | (GenericTypedElement<EncryptedField> & EncryptedElement)
  | (GenericTypedElement<string> &
      (
        | DynamicElement
        | GeneratedElement
        | HiddenElement
        | OptionsElement<Record<string, Option<any>>>
        | StringElement
        | SwitchElement
      ))
>;

type EmptySchema = Readonly<Record<never, Element>>;

export type Component<S extends EmptySchema> = Readonly<
  BaseElement & {
    schema: S;
  }
>;

export type ItemComponent<S extends EmptySchema> = Readonly<Component<S>> &
  GenericTypedElement<string> &
  (DynamicElement | GeneratedElement | SingletonItem | StringElement) & {
    maxItems?: number;
    featureFlag?: string;
    /** Maturity level of this installation component
     *
     * Controls the visibility of feature maturity badges in the UI. When set to "beta" or "preview",
     * a blue "Beta" tag is displayed next to the component label in installation forms and access request modals.
     * When set to "ga" (generally available), no badge is shown. When set to "dev", the component
     * is hidden (and not configurable) outside devLike environments.
     *
     * @see {@link ComponentMaturity} for available values
     */
    readonly maturity: ComponentMaturity;
  };

// --- config generation ---

type OptionField<K, S extends Record<string, EmptySchema>> = Collapsed<
  {
    readonly type: K;
  } & {
    readonly [SS in keyof S]: ElementConfigOf<S[SS]>;
  }
>;

export const isOptionsConfigField = <K>(
  item: unknown
): item is OptionField<K, any> =>
  typeof item === "object" &&
  item !== null &&
  "type" in item &&
  typeof item.type === "string";

type ConvertOption<K, O> =
  O extends Option<infer S> ? OptionField<K, S> : never;

export type OptionsConfigOf<E> =
  E extends OptionsElement<infer O>
    ? {
        [K in keyof O]: ConvertOption<K, O[K]>;
      }[keyof O] // Makes a union type of the individual option types
    : never;

export type EncryptedField = {
  readonly hash?: string;
  readonly clearText?: string;
};
export const isEncryptedField = (value: any): value is EncryptedField =>
  typeof value === "object" &&
  value !== null &&
  ("hash" in value || "clearText" in value) &&
  (!("hash" in value) || typeof value.hash === "string") &&
  (!("clearText" in value) || typeof value.clearText === "string");

export type ElementConfigOf<I> = I extends StringElement
  ? string
  : I extends GeneratedElement
    ? string
    : I extends HiddenElement
      ? string
      : I extends DynamicElement
        ? string
        : I extends EncryptedElement
          ? EncryptedField
          : I extends SwitchElement
            ? boolean
            : OptionsConfigOf<I>;

export type ComponentConfigOf<C> =
  C extends Component<infer S>
    ? {
        readonly [K in keyof S as S[K] extends { optional: true }
          ? never
          : K]: ElementConfigOf<S[K]>;
      } & {
        readonly [K in keyof S as S[K] extends { optional?: true }
          ? K
          : never]?: ElementConfigOf<S[K]>;
      }
    : never;

/** Shared base data for all component installations */
export type BaseComponent = {
  readonly label?: string;
  /** Integration status
   *
   * States are:
   * - (undefined): Installation not started; user is displayed a prompt to
   *   enter a component item identifier
   * - `stage`: User has entered component item identifier; user is displayed
   *   instructions to set up the integration in the 3rd-party system
   * - `configure`: User has set up the integration in the 3rd-party system;
   *   user is show a configuration form
   * - `installed`: P0 backend has fully validated installation and it is
   *   ready to use
   */
  readonly state: Extract<
    IntegrationStatus,
    "configure" | "error" | "installed" | "stage"
  >;
};

/** Transitions between integration states
 *
 * - `assemble`: Transition from no installation to `stage`; assemblers are used
 *   to generate any data needed to present set-up instructions
 * - `verify`: Transition from `stage` to `configure`; verifies that set-up
 *   instructions are run correctly
 * - `configure`: Transition form `configure` to `installed`; validates
 *   configuration settings
 */
export const InstallSteps = {
  assemble: {
    installer: "assembler",
    // Can always start from scratch (undefined means 'all')
    expectedStates: undefined,
    nextState: "stage",
  } as const,
  verify: {
    installer: "verifier",
    // Any defined state
    expectedStates: undefined,
    nextState: "configure",
  } as const,
  configure: {
    installer: "configurer",
    expectedStates: ["configure", "installed"],
    nextState: "installed",
  } as const,
} as const;

export type ItemConfigOf<I> = Collapsed<BaseComponent & ComponentConfigOf<I>>;

/** Creates a configuration data model from an install specification.
 *
 * The data model is constructed according to the rules for {@link ItemComponent}
 * and {@link Element} above.
 */
export type ConfigOf<C> = {
  readonly [K in keyof C]: Readonly<Record<string, ItemConfigOf<C[K]>>>;
};

// --- Installers ---

// Machinery for typing Installers.
//
// Type parameter convention:
//   Domain - defines types that are specific to the install domain (viz., backend or frontend)
//   Root - the installations config option, extends ConfigOf<Install>
//   Item - a specific installed component config with a unique identifier, will extends Root[keyof Root][number]
//   Install - the install metadata specification, extends InstallSpec
//   Field - a specific install field metadatum, is a child of Install

type DomainKey = "backend" | "frontend";

/** Input installation data for an installation domain (e.g. backend or frontend)
 *
 * @param Context - The context type this domain passes to handler functions
 * @param Help - The return type that help functions will return
 */
// eslint-disable-next-line @typescript-eslint/no-unused-vars
export type InstallDomain<Key extends DomainKey, Context, Help> = {
  readonly domain: Key;
  readonly context: Readonly<Context>;
};

type BackendDomain = { domain: "backend" };
type FrontendDomain = { domain: "frontend" };

/** One option presented by a "dynamic" element */
type DynamicOption<Help> = {
  id: string;
  label?: Help;
  /** Displayed beside the option's label */
  iconUrl?: string;
};

/** Constructs a list of options presented by a "dynamic" element */
export type DynamicProvider<Domain, Item> =
  Domain extends InstallDomain<"frontend", infer Context, infer Help>
    ? (
        context: Readonly<Context>,
        item: Readonly<Item>,
        id: string
      ) => Promise<DynamicOption<Help>[]>
    : never;

/** Refreshes the data consumed by a "dynamic" element */
export type DynamicRefresh<Domain> =
  Domain extends InstallDomain<"frontend", infer Context, infer _Help>
    ? (context: Context, id?: string) => void
    : never;

/** Returns valid options for a SelectElement */
export type StaticProvider<
  Domain,
  Item,
  Options extends number | string | symbol,
> =
  Domain extends InstallDomain<"frontend", infer Context, infer _Help>
    ? (
        context: Readonly<Context>,
        item: Readonly<Item>,
        id: string
      ) => Promise<Options[]>
    : never;

type InstructionCommand<Help> = {
  header?: Help;
  command: string;
};

export type Instructions<Help> = {
  help?: Help;
  commands?: Readonly<{
    console?: Help;
    shell?: InstructionCommand<Help>[];
    iac?: InstructionCommand<Help>[];
  }>;
};

/** Enabled dynamic control over the visibility of the element in the UI */
export type Mask<Domain> =
  Domain extends InstallDomain<"frontend", infer Context, infer _Component>
    ? (context: Readonly<Context>, id: string) => boolean
    : never;

/** Provides installation instructions */
export type Instructor<Domain, Item, Field> =
  Domain extends InstallDomain<"frontend", infer Context, infer Help>
    ? (
        context: Readonly<Context>,
        id: string,
        item: Readonly<Item>,
        field: Readonly<Field>,
        state: Readonly<Record<string, any>>,
        setState: (value: Record<string, any>) => any
      ) => Instructions<Help>
    : never;

/** Provides a JSX element to render arbitrary footer content */
export type Footer<Domain, Item> =
  Domain extends InstallDomain<"frontend", infer Context, infer Component>
    ? (
        context: Readonly<Context>,
        id: string,
        item: Readonly<Item>
      ) => Component
    : never;

/** Provides a JSX element to conditionally override error messages */
export type ErrorElementCreator<Domain> =
  Domain extends InstallDomain<"frontend", infer Context, infer _Help>
    ? (context: Readonly<Context>, id: string) => any
    : never;

export type MetadataProvider<Domain, Item, Field> =
  Domain extends InstallDomain<"backend", infer Context, infer _Help>
    ? (
        context: Readonly<Context>,
        id: string,
        item: Readonly<Item>,
        field: Readonly<Field>
      ) => Promise<Partial<Record<string, any>>>
    : never;

/** Provides friendly labels for config elements */
export type Labeler<Domain, Item> =
  Domain extends InstallDomain<infer _DomainKey, infer Context, infer Help>
    ? (
        context: Readonly<Context>,
        id: string,
        item: Readonly<Item>,
        field: Readonly<{ id: string; label?: string }>
      ) => Help
    : never;

/** Returns whether this config meets necessary pre-requisites for installation
 *
 * Truthy values will disable the option and be printed for the user.
 */
export type PrerequisiteMessages<Domain, Item> =
  Domain extends InstallDomain<"frontend", infer Context, infer _Help>
    ? (
        context: Readonly<Context>,
        id: string,
        item: Readonly<Item>
      ) => Promise<any>
    : never;

/** Validates the config based on this user's input
 *
 * After validation the item will be in "installed" state.
 */
type Validator<T> = (id: string, field: T) => Promise<string | undefined>;

/** Updates the config based on this user's input
 *
 * Used when adding or reconfiguring an item. After assembly the item will be in
 * "configure" state.
 */
export type Assembler<Domain, Item, Field> =
  Domain extends InstallDomain<"backend", infer Context, infer _Help>
    ? (
        context: Readonly<Context>,
        id: string,
        item: Readonly<Item>,
        field: Readonly<Field>
      ) => Promise<Partial<Field> | undefined>
    : never;

type BackendInstalls<Domain, Item, Field> = {
  /** Renders step instructions
   */
  metadata?: MetadataProvider<Domain, Item, Field>;
  /** Runs on item creation (zero -> stage)
   */
  assembler?: Assembler<Domain, Item, Field>;
  /** Runs on final install step / update to config (configure -> installed)
   */
  configurer?: Assembler<Domain, Item, Field>;
  /** Runs after user runs setup commands in the integrated environment (stage -> configure)
   */
  verifier?: Assembler<Domain, Item, Field>;

  /** Provides the terraform document that will be inserted into the script for installation via Terraform
   * which can be found in `backend/src/api-service/install/terraform-install-script.ts`
   */
  installTerraform?: () => string;
};

type FrontendInstalls<Domain, Item, Field> = {
  /**
   * Controls the visibility of the element in the UI.
   */
  hidden?: Mask<Domain>;
  /** Renders step instructions
   */
  instructions?: Instructor<Domain, Item, Field>;
  /** Renders config item labels
   */
  labeler?: Labeler<Domain, Item>;
  /** Allows a frontend installer to override the default
   *  error message from the element level validation.
   *  This is useful for enabling the frontend code to
   *  replace the error string from the backend with a
   *  JSX element using links or typography, but any type can
   *  work.
   * */
  errorElement?: ErrorElementCreator<Domain>;
  /**
   * Allows a frontend installer to render an arbitrary footer element.
   */
  footer?: Footer<Domain, Item>;
  /**
   * Renders content alongside the primary submit button (e.g. "Update"), on
   * the configure step.
   */
  submitActions?: Footer<Domain, Item>;
  /**
   * Renders content above the installation, on the configure step.
   *
   * Read from the component installer; entries under `items` are ignored. See
   * `newPreamble` for the "install a new item" step.
   */
  preamble?: Footer<Domain, Item>;
  /**
   * Renders content above the "install a new item" step, before the customer
   * has entered anything. Called with the `_new` sentinel id and an empty item,
   * since no item exists yet.
   *
   * Separate from `preamble` so that adding one here does not change what an
   * existing `preamble` already shows.
   */
  newPreamble?: Footer<Domain, Item>;
};

export type Installs<Domain, Item, Field> = Domain extends BackendDomain
  ? BackendInstalls<Domain, Item, Field>
  : Domain extends FrontendDomain
    ? FrontendInstalls<Domain, Item, Field>
    : object;

type PrerequisiteInstalls<Domain, Item> = Domain extends FrontendDomain
  ? { prerequisiteMessages?: PrerequisiteMessages<Domain, Item> }
  : object;

type StringElementInstalls<Domain, Item, Element> =
  Element extends StringElement ? PrerequisiteInstalls<Domain, Item> : object;

type DynamicElementFrontendInstalls<Domain, Item> =
  Domain extends FrontendDomain
    ? {
        optionProvider: DynamicProvider<Domain, Item>;
        refresh?: DynamicRefresh<Domain>;
      }
    : object;

type DynamicElementInstalls<Domain, Item, Element> =
  Element extends DynamicElement
    ? DynamicElementFrontendInstalls<Domain, Item> &
        PrerequisiteInstalls<Domain, Item>
    : object;

type OptionsElementInstalls<Domain, Root, Item, Element> =
  Element extends OptionsElement<infer O>
    ? {
        optionsProvider?: StaticProvider<Domain, Item, keyof O>;
        options: {
          [OO in keyof O]?: ComponentInstallerOf<Domain, Root, Item, O[OO]> &
            PrerequisiteInstalls<Domain, Item>;
        };
      }
    : object;

export type ElementInstaller<Domain, Root, Item, Element> = Collapsed<
  OptionsElementInstalls<Domain, Root, Item, Element> &
    DynamicElementInstalls<Domain, Item, Element> &
    StringElementInstalls<Domain, Item, Element> &
    Installs<Domain, Item, ElementConfigOf<Element>>
>;

export type ComponentSchemaInstalls<Domain, Root, Item, Schema> =
  Schema extends Record<string, never>
    ? object
    : {
        items: {
          [K in keyof Schema]?: ElementInstaller<Domain, Root, Item, Schema[K]>;
        };
      };

export type ComponentInstaller<Domain, Root, Item, This, SchemaEntry> =
  Collapsed<
    Installs<Domain, Item, ItemConfigOf<This>> &
      ComponentSchemaInstalls<Domain, Root, Item, SchemaEntry> &
      StringElementInstalls<Domain, Item, This> &
      DynamicElementInstalls<Domain, Item, This>
  >;

type ComponentInstallerOf<Domain, Root, Item, This> =
  This extends Component<infer S>
    ? ComponentInstaller<Domain, Root, Item, This, S>
    : never;

/** Defines how a config is installed.
 *
 * Each element of the configuration's {@link InstallSpec} is mapped into this
 * data structure, with each entry defining which instructions to display,
 * any pre-requisites that must be met in order to install, and any validation
 * to run after user configuration.
 */
export type ItemInstallerOf<
  Domain extends InstallDomain<DomainKey, any, any>,
  Install,
> = {
  [K in keyof Install]: ComponentInstallerOf<
    Domain,
    ConfigOf<Install>,
    ConfigOf<Install>[K][string],
    Install[K]
  >;
};
